import {
  geometryBounds,
  rectangleFromPoints,
  translateGeometry,
} from './markupGeometry'
import type { AnnotationGeometry, MarkupPoint } from './types'

export interface DisplaySize {
  width: number
  height: number
}

export interface DisplayBox extends DisplaySize {
  x: number
  y: number
}

const finitePositive = (value: number) => Number.isFinite(value) && value > 0

export const pointFromClient = (
  clientX: number,
  clientY: number,
  bounds: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>
): MarkupPoint | null => {
  if (!finitePositive(bounds.width) || !finitePositive(bounds.height))
    return null
  const point = {
    x: (clientX - bounds.left) / bounds.width,
    y: (clientY - bounds.top) / bounds.height,
  }
  return Number.isFinite(point.x) && Number.isFinite(point.y) ? point : null
}

export const annotationBounds = (
  geometry: AnnotationGeometry,
  display: DisplaySize,
  pointRadius: number
): DisplayBox => {
  if (geometry.shape === 'point') {
    return {
      x: geometry.x * display.width,
      y: geometry.y * display.height,
      width: pointRadius * 2,
      height: pointRadius * 2,
    }
  }
  const { x_min, y_min, x_max, y_max } = geometryBounds(geometry)
  return {
    x: x_min * display.width,
    y: y_min * display.height,
    width: (x_max - x_min) * display.width,
    height: (y_max - y_min) * display.height,
  }
}

export const geometryFromDisplayBox = (
  original: AnnotationGeometry,
  origin: DisplayBox,
  next: DisplayBox,
  display: DisplaySize,
  minimumSize?: { width: number; height: number }
): AnnotationGeometry | null => {
  if (!finitePositive(display.width) || !finitePositive(display.height))
    return null
  const moved = {
    x: (next.x - origin.x) / display.width,
    y: (next.y - origin.y) / display.height,
  }
  if (
    original.shape !== 'rectangle' ||
    (next.width === origin.width && next.height === origin.height)
  )
    return translateGeometry(original, { x: 0, y: 0 }, moved)
  const clamp = (value: number) => Math.min(1, Math.max(0, value))
  return rectangleFromPoints(
    {
      x: clamp(next.x / display.width),
      y: clamp(next.y / display.height),
    },
    {
      x: clamp((next.x + next.width) / display.width),
      y: clamp((next.y + next.height) / display.height),
    },
    minimumSize
  )
}
